/**
 * React hook for using the modular API client
 *
 * Provides type-safe methods for all API endpoints with domain-based organization.
 *
 * Usage:
 * ```typescript
 * const { apiClient } = useApiClient();
 *
 * // New API (recommended)
 * const projects = await apiClient.projects.getProjects();
 * const record = await apiClient.records.getRecordById('123');
 * const scripts = await apiClient.scripts.getScripts();
 *
 * // Legacy API (deprecated, for backward compatibility)
 * const projects = await apiClient.projects.getProjects();
 * ```
 *
 * Phase 4.2: Create centralized API client
 * Refactored: Modular domain-based architecture
 */

import { useMemo } from 'react';
import { useAuthFetch } from './useAuthFetch';
import { ApiClient, API_ENDPOINTS } from '@/lib/api';

/**
 * Hook for accessing the centralized API client
 *
 * Returns:
 * - apiClient: Type-safe API client with methods for all endpoints
 * - endpoints: API endpoint constants (for backward compatibility)
 */
export function useApiClient() {
  const { authenticatedFetch, getAuthData } = useAuthFetch();

  // Create ApiClient instance (memoized to prevent recreation on every render)
  const apiClient = useMemo(() => {
    return new ApiClient(authenticatedFetch, undefined, getAuthData);
  }, [authenticatedFetch, getAuthData]);

  return {
    apiClient,
    endpoints: API_ENDPOINTS,
  };
}
